import { s, c, nextAppRouter } from '../../../../val.config';
import benefitsSectionVal from '../../../../data/benefits.val';
import employeeListVal from '../../../../data/employees.val';
import { pageMetadataSchema } from '../../../../schema/pageMetadata.val';
import {
  singleMediaElementSchema,
  doubleMediaElementSchema,
  linksElementSchema,
  textElementSchema,
  textAndImageElementSchema,
} from '../../../../schema/pageElements.val';

const pageElementsSchema = s.array(
  s.union(
    'type',
    singleMediaElementSchema,
    doubleMediaElementSchema,
    textAndImageElementSchema,
    textElementSchema,
    linksElementSchema
  )
);

export const positionSchema = s
  .object({
    header: s.string(),
    pageImage: s.image(),
    ingress: s.richtext({
      bold: true,
      italic: true,
      lineThrough: true,
      h1: false,
      h2: false,
      h3: true,
      h4: true,
      h5: true,
      h6: true,
      ul: true,
      ol: true,
      a: true,
      img: true,
    }),
    details: s.array(
      s.object({
        title: s.string(),
        text: s.string(),
      })
    ),
    applyUrl: s.object({
      text: s.string(),
      href: s.string(),
    }),
    pageElements: pageElementsSchema,
    benefit: s.keyOf(benefitsSectionVal),
    contact: s.object({
      title: s.string(),
      link: s.object({
        text: s.string(),
        href: s.string(),
      }),
      person: s.keyOf(employeeListVal),
    }),
    pageMetadata: pageMetadataSchema,
  })
  .preview(({ val }) => {
    return {
      title: val.header,
    };
  });

export const allPositionsSchema = s.router(nextAppRouter, positionSchema);

export default c.define(
  '/app/(main)/jobb/[slug]/page.val.ts',
  allPositionsSchema,
  {
    '/jobb/student': {
      header: 'Deltidsjobb for studenter',
      pageImage: {
        path: '/public/val/images/f674e74548ccb0af57c8c8d3d8246fc9c6e1e22d-1129x627.webp',
        width: 1129,
        height: 627,
        mimeType: 'image/webp',
      },
      contact: {
        person: 'lars-ive',
        title: 'Har du spørsmål om stillingen? ',
        link: {
          text: 'Ta kontakt for en hyggelig prat',
          href: '/kaffeprat',
        },
      },
      applyUrl: {
        text: 'Søk nå',
        href: 'https://blankno.recruitee.com',
      },
      pageElements: [
        {
          text: [
            {
              tag: 'p',
              children: [
                {
                  tag: 'span',
                  styles: ['bold'],
                  children: ['Oppstart 2. januar 2025.'],
                },
                'Vårjobb i Blank starter med 2 dager kick-off i Oslo 2. og 3. januar. Etter det vil du jobbe omtrent 7,5 time i uka - med fleksibel arbeidstid- og sted. Jobben varer frem til slutten av april der vi runder av med tre dager i Oslo 28.-30. april.',
                {
                  tag: 'span',
                  styles: ['bold'],
                  children: ['Søknadsfrist tirsdag 1. oktober 2025'],
                },
              ],
            },
          ],
          title: `Hvor og når?`,
          type: 'textElement',
        },
        {
          text: [
            {
              tag: 'p',
              children: [
                'Blank er et design- og teknologimiljø i Oslo som utvikler produkter og tjenester for kunder, i tillegg til at vi bygger ting selv. Vi skal nå lage enda flere egne greier og ser etter studenter som har lyst til å være med på produktutviklingen helt fra starten.',
              ],
            },
            {
              tag: 'p',
              children: [
                'Sammen med deg og de andre vårstudentene skal vi bygge et helt nytt digitalt produkt. Ikke bare fordi det er gøy (det er det), men også fordi vi tror vi har en idé som vil løse problemer for folk. Om vi lykkes og det blir et eget produktselskap vil du også få en eierandel i dette.',
              ],
            },
            {
              tag: 'p',
              children: [
                {
                  tag: 'span',
                  styles: ['bold'],
                  children: ['Vi tror våren ser cirka slik ut'],
                },
              ],
            },
            {
              tag: 'ul',
              children: [
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'p',
                      children: [
                        'To dagers oppstart hos Blank ved Youngstorget i Oslo',
                      ],
                    },
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'p',
                      children: [
                        'En dag i uken gjennom våren - enten på kontoret til Blank eller remote',
                      ],
                    },
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'p',
                      children: [
                        'To fellessamlinger i Oslo (dato tbd.) hvor Blank betaler reise og opphold',
                      ],
                    },
                  ],
                },
              ],
            },
          ],
          title: `Dette vil du jobbe med`,
          type: 'textElement',
        },
        {
          text: [
            {
              tag: 'p',
              children: [
                'Du er design- eller teknologistudent som går i 4. klasse våren 2025. Du bør være naturlig nysgjerrig og ønsker å utfordre deg selv. Du synes det er spennende med digital produktutvikling og liker å bygge ting sammen med andre. Du er ivrig etter å teste idéer og konsepter.',
              ],
            },
            {
              tag: 'p',
              children: [
                { tag: 'span', styles: ['bold'], children: ['Er du designer'] },
                ' ønsker vi at du liker å designe digitale prototyper, teste og evaluere. Du håndterer å ta egne valg basert på eksisterende innsiktsarbeid.',
              ],
            },
            {
              tag: 'p',
              children: [
                { tag: 'span', styles: ['bold'], children: ['Er du teknolog'] },
                ' ønsker vi at du har praktisk erfaring med å bygge applikasjoner.',
              ],
            },
            {
              tag: 'p',
              children: [
                'Du må ha mulighet til å være fysisk tilstede to oppstartsdager i januar.',
              ],
            },
          ],
          title: `Dette ønsker vi fra deg`,
          type: 'textElement',
        },
      ],
      pageMetadata: {
        title: 'Deltidsjobb for studenter',
        description: '',
        image: null,
      },
      benefit: 'vårjobb',
      ingress: [
        {
          tag: 'p',
          children: [
            'I Blank liker vi å lage våre egne greier. Bli med å designe og utvikle et helt nytt digitalt produkt våren 2025.',
          ],
        },
      ],
      details: [],
    },
    '/jobb/teknolog': {
      header: `Bli teknolog i Blank`,
      details: [
        { title: 'Søknadsfrist', text: 'Snarest' },
        { title: 'Ansettelsesform', text: 'Fast' },
      ],
      pageImage: {
        path: '/public/val/2022-02innedagpablank11.februar-46_6650e.jpg',
        width: 2048,
        height: 1366,
        mimeType: 'image/jpeg',
        hotspot: { x: 0.6893732970027248, y: 0.35133409932337545 },
        alt: 'Kristine forteller ivrig om sitt siste oppdrag',
      },
      contact: {
        person: 'magne',
        title: 'Har du spørsmål om stillingen? ',
        link: {
          text: 'Ta kontakt for en hyggelig prat',
          href: '/kaffeprat',
        },
      },
      applyUrl: {
        text: 'Søk nå',
        href: 'https://blank.teamtailor.com/jobs/6297216-bli-teknolog-i-blank',
      },
      pageElements: [
        {
          text: [
            {
              tag: 'p',
              children: [
                'Hos oss vil du jobbe med noen av de flinkeste menneskene du vil møte i din karriere. De deler kunnskap, heier på deg og bygger sammen med deg produkter som faktisk betyr noe. Du kommer til å jobbe med alt fra startups til store selskaper og offentlig sektor. Noen av de aktuelle er Aneo, Aidn, Finn, Handelshøyskolen BI, NRK og WeWillWrite.',
              ],
            },
          ],
          title: 'Hva får du hos Blank?',
          type: 'textElement',
        },
        {
          text: [
            {
              tag: 'ul',
              children: [
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'p',
                      children: ['Har 4+ års erfaring som utvikler'],
                    },
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'p',
                      children: [
                        'Har erfaring som konsulent eller lignende roller',
                      ],
                    },
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'p',
                      children: [
                        'Har fokus på godt håndverk og skriver kode du er stolt av',
                      ],
                    },
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'p',
                      children: [
                        'Trives med å levere kontinuerlig verdi, ser muligheter for forbedring og gjør noe med det',
                      ],
                    },
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'p',
                      children: [
                        'Synes det er spennende å skape løsninger i samarbeid med andre og liker å jobbe tett med både designere og kunder',
                      ],
                    },
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'p',
                      children: [
                        'Har evnen til å løse kundens utfordringer og utfordre kunden når det trengs',
                      ],
                    },
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'p',
                      children: [
                        'Tar initiativ, nøster opp i problemer og viser vei',
                      ],
                    },
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'p',
                      children: [
                        'Har en relevant bachelor- eller mastergrad innen IT, eller tilsvarende erfaring',
                      ],
                    },
                  ],
                },
              ],
            },
            {
              tag: 'p',
              children: [
                'Kjenner du deg igjen i mye, men ikke alt? Søk likevel. Vi ser etter folk – ikke sjekklister.',
              ],
            },
          ],
          title: 'Dette ser vi etter hos deg:',
          type: 'textElement',
        },
      ],
      pageMetadata: {
        title: 'Bli teknolog i Blank',
        description:
          'Vi liker folk som tør, og som koser seg med å løse skikkelige problemer. Det kalles også for engasjerte og ambisiøse utviklere. Vi har dem, men trenger likevel flere!',
        image: null,
      },
      ingress: [
        {
          tag: 'p',
          children: [
            'Vi ser etter hands-on utviklere som både kan skrive solid kode og drive prosjekter fremover. Noen som trives med å jobbe tett med designere, produktledere og kunder og som skjønner at teknologi er mer enn bare kode. Det handler om mennesker, valg og verdi.',
          ],
        },
      ],
      benefit: 'ansatt',
    },
    '/jobb/designer': {
      header: 'Erfaren designer',
      benefit: 'ansatt',
      pageImage: {
        path: '/public/val/2022-05blanks7-arsjubileum(blank-kamera)-8_e1879.jpg',
        width: 3000,
        height: 2000,
        mimeType: 'image/jpeg',
        hotspot: { x: 0.5316666666666666, y: 0.395 },
        alt: 'Bilde av folk',
      },
      contact: {
        person: 'bernholdt',
        title: 'Har du spørsmål? ',
        link: {
          text: 'Ta kontakt for en hyggelig prat',
          href: '/kaffeprat',
        },
      },
      applyUrl: {
        text: 'Søk nå!',
        href: 'https://blank.teamtailor.com/jobs/6297263-bli-designer-i-blank',
      },
      pageElements: [
        {
          type: 'textElement',
          title: 'Hvorfor Blank',
          text: [
            {
              tag: 'p',
              children: [
                'Hos oss vil du jobbe med noen av de flinkeste menneskene du vil møte i din karriere. Folk som deler kunnskap, heier på deg og sammen bygger produkter som faktisk betyr noe.',
              ],
            },
          ],
        },
        {
          type: 'textElement',
          title: 'Du vil jobbe med',
          text: [
            {
              tag: 'p',
              children: [
                'Du vil jobbe med å designe digitale produkter og tjenester. Du får også tid til å lære deg nye ting, og til å bli enda bedre på det du allerede kan. Slik har vi bygget et prisvinnende fagmiljø innen design.',
              ],
            },
          ],
        },
        {
          text: [
            {
              tag: 'p',
              children: [
                'Du kommer til å jobbe med alt fra startups til store selskaper og offentlig sektor. Blant ',
                { tag: 'a', href: '/arbeider', children: ['kundene'] },
                ' våre er Aneo, Finn, ice, Handelshøyskolen BI, NAV og NRK, samt spennende startups som WeWillWrite og Take Take Take. Vi utvikler også egne digitale produkter. Som CMS-et Val som denne nettsiden er bygget på.',
              ],
            },
          ],
          title: 'Kundene våre',
          type: 'textElement',
        },
        {
          text: [
            {
              tag: 'p',
              children: [
                'Du utforsker, undersøker og driver fagfeltet vårt fremover. Du er en dyktig håndverker og formgiver og du er trygg på prosessene og metodene du trenger for å løse kundenes problemer.',
                { tag: 'br' },
              ],
            },
            {
              tag: 'p',
              children: [
                'Fordi vi tar ansvar for hele designprosessen må du være god på å både forstå problemet og til å finne, designe, prototype og evaluere løsningen.',
                { tag: 'br' },
                { tag: 'br' },
                'I tillegg ønsker vi at du har:',
                { tag: 'br' },
              ],
            },
            {
              tag: 'ul',
              children: [
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'p',
                      children: [
                        '4+ års erfaring som designer av digitale produkter',
                      ],
                    },
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'p',
                      children: [
                        'Erfaring fra konsulentrolle eller tilsvarende',
                      ],
                    },
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'p',
                      children: [
                        'Mastergrad innen design eller tilsvarende erfaring',
                      ],
                    },
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'p',
                      children: [
                        'Evne til å lede designprosesser og drive prosjekter fremover',
                      ],
                    },
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'p',
                      children: [
                        'Erfaring fra tverrfaglige team med teknologer og produktledere',
                      ],
                    },
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'p',
                      children: ['God kommunikasjonsevne på norsk og engelsk'],
                    },
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'p',
                      children: [
                        'En solid håndverker sombruker innsikt aktivt',
                      ],
                    },
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'p',
                      children: [
                        'Du er opptatt av å skape løsninger som faktisk fungerer',
                        { tag: 'br' },
                      ],
                    },
                  ],
                },
              ],
            },
            {
              tag: 'p',
              children: [
                'Kjenner du deg igjen i mye, men ikke alt? Søk likevel. Vi ser etter folk – ikke sjekklister.',
              ],
            },
          ],
          title: 'Dette ser vi etter',
          type: 'textElement',
        },
      ],
      pageMetadata: {
        title: 'Bli designer i Blank',
        description:
          'Bli med i et prisvinnende fagmiljø av digitale designere som setter agendaen innen digital produktutvikling.',
        image: null,
      },
      ingress: [
        {
          tag: 'p',
          children: [
            'Vi ser etter erfarne designere som ønsker å formgi digitale løsninger og ta ansvar for å drive prosjekter fremover. Du må trives med å jobbe tett med utviklere, produktledere og kunder.',
          ],
        },
      ],
      details: [
        { title: 'Søknadsfrist', text: 'Snarest' },
        { title: 'Ansettelsesform', text: 'Fast' },
        { text: '4+ år', title: 'Erfaringsnivå' },
      ],
    },
    '/jobb/nyutdannet-designer-2025': {
      header: 'Nyutdannet interaksjonsdesigner 2025',
      pageImage: {
        path: '/public/val/images/3064b5e40967f1d1bea1d8dd95efa41ca26c96b0-1920x1281.webp',
        width: 1920,
        height: 1281,
        mimeType: 'image/webp',
      },
      contact: {
        person: 'lars-ive',
        title: 'Har du spørsmål om stillingen? ',
        link: {
          text: 'Ta kontakt for en hyggelig prat',
          href: '/kaffeprat',
        },
      },
      applyUrl: {
        text: 'Søk nå!',
        href: 'https://blankno.recruitee.com/o/nyutdannede-designer-2024/c/new',
      },
      pageElements: [
        {
          text: [
            {
              tag: 'p',
              children: [
                'Vi i Blank designer og utvikler digitale tjenester. Vi jobber med en kombinasjon av start-ups, små og store virksomheter, og i tillegg utvikler vi egne produkter og tjenester når vi kommer på noe bra – mest fordi vi synes det er gøy. En av disse tjenestene er nå et eget firma med 14 ansatte 🎉',
              ],
            },
            {
              tag: 'p',
              children: [
                'Du kan ',
                {
                  tag: 'a',
                  href: 'https://blogg.blank.no/tagged/whatitdo',
                  children: [
                    'se noen eksempler på ting vi jobber med på bloggen',
                  ],
                },
                ', som Amoi, ice, Karpe, Reisen til Spektrum (Sondre Justad), Aneo og reMarkable. Tre år på rad er også tjenester Blank har utviklet blitt nominert til Gulltaggen.',
              ],
            },
          ],
          title: `Dette vil du jobbe med`,
          type: 'textElement',
        },
        {
          text: [
            {
              tag: 'p',
              children: [
                'Vi har blant annet satt av første fredag i måneden til innedag, en hel dag der du kan lære deg nye ting eller bli enda bedre på ting du allerede kan. I tillegg til dette kan du selvfølgelig dra på konferanser og kurs når ',
                { tag: 'span', styles: ['italic'], children: ['du'] },
                ' mener det er en god ide.',
              ],
            },
          ],
          title: `Faglig utvikling`,
          type: 'textElement',
        },
        {
          text: [
            {
              tag: 'p',
              children: [
                'Du bør være trygg på de prosessene og metodene du trenger til å løse kundenes utfordringer, og du bør ha en trang til å utforske, undersøke og drive fagfeltet vårt fremover. Vi tror på styrken i dybde og mangfold og er ikke interessert i å ansette mer av oss selv. Vi ønsker å bli utfordret, og at du trekker oss i nye retninger.',
              ],
            },
          ],
          title: `Dette ønsker vi fra deg`,
          type: 'textElement',
        },
        {
          text: [
            {
              tag: 'p',
              children: [
                'Du er nysgjerrig, motivert og selvdreven, fordi vi stoler på deg og gir deg tillit til å kjøre på!',
              ],
            },
            {
              tag: 'ul',
              children: [
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'p',
                      children: [
                        'Du må være dyktig til å kommunisere både visuelt og muntlig, fordi det er viktig å kunne overbevise folk om at det du har laget er en sjukt god idé.',
                      ],
                    },
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'p',
                      children: [
                        'Du må ha grundig forståelse for designprosess og metodikk, fordi… les punktet over.',
                      ],
                    },
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'p',
                      children: [
                        'Du må trives med å lede designprosesser fra start til slutt, fordi samskaping er en viktig del av å være designer.',
                      ],
                    },
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'p',
                      children: [
                        'Du må ønske å ta faglig ansvar, både internt og på prosjekt, fordi vi selger kunnskap og da må vi lære noe nytt hele tiden.',
                      ],
                    },
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'p',
                      children: [
                        'Hvis du ikke kan krysse av på alle punktene, stryker du dem og fyller på med noen egne!',
                      ],
                    },
                  ],
                },
              ],
            },
          ],
          title: `og ellers...`,
          type: 'textElement',
        },
        {
          text: [
            {
              tag: 'p',
              children: [
                {
                  tag: 'span',
                  styles: ['bold'],
                  children: ['Oppstart august 2025'],
                },
                '. ',
              ],
            },
            {
              tag: 'p',
              children: [
                'Det starter med to uker onboarding, etter dette er målet at du går direkte ut i prosjekt sammen med andre fra Blank.',
              ],
            },
            {
              tag: 'p',
              children: [
                {
                  tag: 'span',
                  styles: ['bold'],
                  children: ['Søknadsfrist 1. oktober 2024'],
                },
              ],
            },
          ],
          title: `Hvor og når?`,
          type: 'textElement',
        },
        {
          text: [
            {
              tag: 'p',
              children: [
                'Blank har en todelt prosess for designere. Hvor vi i første del gjør et dypdykk i din CV og portefølje. Og du får også en caseoppgave tre dager før intervjuet som du skal presentere ditt løsningsforslag på.',
              ],
            },
            {
              tag: 'p',
              children: [
                'De kandidatene som går videre får del to av caseoppgave, også denne tre dager før andre og siste intervjurunde.',
              ],
            },
          ],
          title: `Søknadsprosess`,
          type: 'textElement',
        },
        {
          type: 'linksElement',
          links: [
            {
              href: '/jobb/intervjuprosess-design',
              text: 'Vår intervjuprosess',
            },
          ],
        },
      ],
      pageMetadata: {
        title: '',
        description: '',
        image: null,
      },
      benefit: 'betingelser',
      ingress: [
        {
          tag: 'p',
          children: [
            'Vi har startet å lete etter nyutdannede interaksjonsdesignere for oppstart i 2025. ',
          ],
        },
        {
          tag: 'p',
          children: [
            {
              tag: 'span',
              styles: ['bold'],
              children: ['Søknadsfrist 1. oktober 2024!'],
            },
          ],
        },
      ],
      details: [],
    },
    '/jobb/intervjuprosess-design': {
      header: 'Slik er vår intervjuprosess for designere',
      pageImage: {
        path: '/public/val/images/3064b5e40967f1d1bea1d8dd95efa41ca26c96b0-1920x1281.webp',
        width: 1920,
        height: 1281,
        mimeType: 'image/webp',
      },
      contact: {
        person: 'lars-ive',
        title: 'Har du spørsmål om stillingen? ',
        link: {
          text: 'Ta kontakt for en hyggelig prat',
          href: '/kaffeprat',
        },
      },
      applyUrl: {
        text: 'Søk nå!',
        href: 'https://blankno.recruitee.com/o/designer/',
      },
      pageElements: [
        {
          text: [
            {
              tag: 'p',
              children: [
                'Send oss en søknad hvor du beskriver hvem du er som designer, hva som motiverer deg til å søke hos Blank og gjerne litt om hva du kan tenke deg å jobbe med (om du vet det).',
              ],
            },
            {
              tag: 'p',
              children: [
                'Vi ønsker oss en rett frem og enkel CV, bruk gjerne en Word-mal til dette. Vi leser mange CV’er og det hjelper oss veldig at disse ikke avviker fra standarden. Legg heller designkruttet ditt i porteføljen.',
              ],
            },
            {
              tag: 'p',
              children: [
                'Porteføljen bør inneholde masse gode bilder av hva du har skapt som designer, vi vil også se konkrete eksempler på din design prosess. Om du har lyst til å kline til sleng gjerne med hvilken effekt designet du skapte hadde!',
              ],
            },
          ],
          title: 'Du søker - vi lover at du hører fra oss!',
          type: 'textElement',
        },
        {
          text: [
            {
              tag: 'p',
              children: [
                'Blir du invitert til et intervju, får du en caseoppgave tre dager før intervjuet. Hvordan du bruker tiden er opp til deg.',
              ],
            },
            {
              tag: 'p',
              children: [
                'Her ønsker vi å få et innblikk i hvordan du tar valg, strukturerer din prosess og jobber med prosess som designer.',
              ],
            },
            {
              tag: 'p',
              children: [
                'Dette er en oppgave for å ha noe å snakke omkring og gi struktur til en dialog med oss om dine designferdigheter.',
              ],
            },
          ],
          title: 'Første caseoppgave - du tenker litt',
          type: 'textElement',
        },
        {
          text: [
            {
              tag: 'p',
              children: [
                'Du møter vanligvis to designere fra Blank og det varer i ca. 90 minutter. Vi starter med en gjennomgang av din portefølje og CV, vi vil se hvem du er som designer, hva du liker å drive med og hva som driver deg.',
              ],
            },
            {
              tag: 'p',
              children: [
                'Deretter presenterer du caseoppgaven din, tenk at vi er kunden. Etterpå ønsker vi en diskusjonsbasert og ganske uformell dialog om din løsning.',
              ],
            },
            {
              tag: 'p',
              children: [
                'Målet er å forstå dine refleksjoner og erfaringer, så vi spør og graver. Vi ser ikke etter «riktige» svar, men er mer nysgjerrig på hvordan du tenker.',
              ],
            },
            {
              tag: 'p',
              children: [
                'Til slutt får du godt med tid til å spørre oss om det du måtte lure på, hvor vi kan fortelle om hvordan det er å være designer i Blank, hvordan vi liker å jobbe og hvordan vi har det hos kundene våre.',
              ],
            },
          ],
          title: '1 - Første intervju - Vi blir litt kjent',
          type: 'textElement',
        },
        {
          text: [
            {
              tag: 'p',
              children: [
                'Går du videre etter første intervju, vil du motta den andre delen av caseoppgaven tre dager før andre intervju. I denne oppgaven er vi ute etter å se mer av ditt håndverk som designer, hvordan du bygger og designer konkrete løsninger, og hvordan du vurderer ditt eget design fortløpende.',
              ],
            },
          ],
          title: 'Andre caseoppgave - du tenker litt mer',
          type: 'textElement',
        },
        {
          text: [
            {
              tag: 'p',
              children: [
                'Som oftest er det to designere fra Blank, hvor designleder Jon Bernholdt alltid er med. Det varer vanligvis i 90 minutter.',
              ],
            },
            {
              tag: 'p',
              children: [
                'Her er det caseoppgaven som er hovedfokus og vi ønsker igjen at du presenterer den som om vi er kunden, før vi diskuterer løsningen og hvordan du har jobbet den frem.',
              ],
            },
            {
              tag: 'p',
              children: [
                'Det er ganske vanlig at Jon også ønsker å ta en titt på porteføljen og snakke seg litt gjennom den med deg.',
              ],
            },
          ],
          title: '2 - Andre intervju - Du blir kjent med flere av oss',
          type: 'textElement',
        },
        {
          text: [
            {
              tag: 'p',
              children: [
                'Føler vi oss trygge på at du kan være et bra tilskudd for Blank og at du kommer til å trives sammen med oss vil vi gi deg et tilbud.',
              ],
            },
            {
              tag: 'p',
              children: ['Her håper vi jo såklart at du velger oss!'],
            },
          ],
          title: '3 - Vi sender deg et tilbud',
          type: 'textElement',
        },
        {
          text: [
            {
              tag: 'p',
              children: [
                'Når du har signert pleier vi ganske kjapt å invitere deg til Slack og alt av sosiale ting, sånn at du får muligheten til å se og bli kjent med folk!',
              ],
            },
          ],
          title: '4 - Signering',
          type: 'textElement',
        },
        {
          text: [
            {
              tag: 'p',
              children: [
                'Du møter daglig leder Jahn Arne og går gjennom detaljene.',
              ],
            },
          ],
          title: 'Kontraktsmøte',
          type: 'textElement',
        },
      ],
      pageMetadata: {
        title: '',
        description: '',
        image: null,
      },
      benefit: 'ansatt',
      ingress: [
        {
          tag: 'p',
          children: [
            'Send oss en søknad hvor du beskriver hvem du er som designer, hva som motiverer deg til å søke hos Blank og gjerne litt om hva du kan tenke deg å jobbe med (om du vet det).',
          ],
        },
        {
          tag: 'p',
          children: [
            'Vi ønsker oss en rett frem og enkel CV, bruk gjerne en Word-mal til dette. Vi leser mange CV’er og det hjelper oss veldig at disse ikke avviker fra standarden. Legg heller designkruttet ditt i porteføljen.',
          ],
        },
        {
          tag: 'p',
          children: [
            'Porteføljen bør inneholde masse gode bilder av hva du har skapt som designer, vi vil også se konkrete eksempler på din design prosess. Om du har lyst til å kline til sleng gjerne med hvilken effekt designet du skapte hadde!',
          ],
        },
      ],
      details: [],
    },
    '/jobb/nyutdannet-teknolog-2025': {
      header: 'Nyutdannet teknolog 2025',
      pageImage: {
        path: '/public/val/images/f674e74548ccb0af57c8c8d3d8246fc9c6e1e22d-1129x627.webp',
        width: 1129,
        height: 627,
        mimeType: 'image/webp',
      },
      contact: {
        person: 'clara',
        title: 'Har du spørsmål om stillingen? ',
        link: {
          text: 'Ta kontakt for en hyggelig prat',
          href: '/kaffeprat',
        },
      },
      applyUrl: {
        text: 'Søk nå!',
        href: 'https://blankno.recruitee.com/o/nyutdannet-teknolog-2025',
      },
      pageElements: [
        {
          text: [
            {
              tag: 'p',
              children: [
                'Vi i Blank designer og utvikler digitale tjenester. Vi jobber med en kombinasjon av start-ups, små og store virksomheter, og i tillegg utvikler vi egne produkter og tjenester når vi kommer på noe bra – mest fordi vi synes det er gøy. En av disse tjenestene er nå et eget firma med 14 ansatte 🎉  ',
              ],
            },
            {
              tag: 'p',
              children: [
                'Du kan ',
                {
                  tag: 'a',
                  href: 'https://blogg.blank.no/tagged/whatitdo',
                  children: [
                    'se noen eksempler på ting vi jobber med på bloggen',
                  ],
                },
                ', som Amoi, ice, B-Boy Myhre, Reisen til Spektrum (Sondre Justad), Aneo og reMarkable. Tre år på rad er også tjenester Blank har utviklet blitt nominert til Gulltaggen.',
              ],
            },
          ],
          title: `Dette vil du jobbe med`,
          type: 'textElement',
        },
        {
          text: [
            {
              tag: 'p',
              children: [
                'Vi har blant annet satt av første fredag i måneden til innedag, en hel dag der du kan lære deg nye ting eller bli enda bedre på ting du allerede kan. I tillegg til dette kan du selvfølgelig dra på konferanser og kurs når ',
                { tag: 'span', styles: ['italic'], children: ['du'] },
                ' mener det er en god ide.',
              ],
            },
          ],
          title: `Faglig utvikling`,
          type: 'textElement',
        },
        {
          text: [
            {
              tag: 'p',
              children: [
                'Du bør være en selvdreven person som takler at vi er i utvikling. Du bør være trygg på egne ferdigheter, du bør ha en trang til å utforske og undersøke, og du må kunne være med å drive fagfeltet vårt fremover.',
              ],
            },
            { tag: 'p', children: ['Og ellers...'] },
            {
              tag: 'ul',
              children: [
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'p',
                      children: [
                        'Vi håper du er nysgjerrig, motivert og selvdreven – fordi vi stoler på deg og gir deg tillit til å kjøre på!',
                      ],
                    },
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'p',
                      children: [
                        'Du må ønske å ta faglig ansvar, både internt og på prosjekt, fordi vi selger kunnskap og da må vi lære noe nytt hele tiden.',
                      ],
                    },
                  ],
                },
              ],
            },
          ],
          title: `Dette ønsker vi fra deg`,
          type: 'textElement',
        },
        {
          text: [
            {
              tag: 'p',
              children: [
                {
                  tag: 'span',
                  styles: ['bold'],
                  children: ['Oppstart august 2025'],
                },
                '.',
              ],
            },
            {
              tag: 'p',
              children: [
                'Det starter med to uker onboarding, etter dette er målet at du går direkte ut i prosjekt sammen med andre fra Blank.',
              ],
            },
            {
              tag: 'p',
              children: [
                'Vi forventer at første runde intervjuer vil skje mellom 15. - 19. juli.',
              ],
            },
            {
              tag: 'p',
              children: [
                {
                  tag: 'span',
                  styles: ['bold'],
                  children: ['Søknadsfrist mandag 1. juli 2024'],
                },
              ],
            },
          ],
          title: `Hvor og når?`,
          type: 'textElement',
        },
        {
          text: [
            {
              tag: 'ul',
              children: [
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'p',
                      children: [
                        'Første steg i prosessen er en kodequiz som vi sender deg kort tid etter du sendte inn søknaden din. Sammen med CV og resultatet av kodequiz kommer vi til å ta en vurdering om vi inviterer deg videre til neste intervjurunde.',
                      ],
                    },
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'p',
                      children: [
                        'I det første intervjuet forteller vi om Blank og prater om dine spørsmål, du forteller om dine prosjekterfaringer og hva du ønsker å jobbe med. Intervjuet kommer til å ta litt over en time.',
                      ],
                    },
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'p',
                      children: [
                        'Siste steg i prosessen er et programmeringsintervju. Du kommer til å sitte sammen med en av oss og prøve å løse en programmeringsoppgave. Dette tar ca 2 timer.',
                      ],
                    },
                  ],
                },
              ],
            },
            {
              tag: 'p',
              children: [
                'Vurderingen om du kommer videre til første intervjurunden skjer etter at søknadsfristen har utløpet, men send Clara gjerne en e-post om du trenger en raskere søknadsprosess.',
              ],
            },
          ],
          title: `Søknadsprosess`,
          type: 'textElement',
        },
      ],
      pageMetadata: {
        title: '',
        description: '',
        image: null,
      },
      benefit: 'betingelser',
      ingress: [
        {
          tag: 'p',
          children: [
            'Vi har startet å lete etter nyutdannede teknologer for oppstart i august 2025. ',
          ],
        },
        {
          tag: 'p',
          children: [
            'For å gi dere som får tilbud i forbindelse med sommerjobb muligheten til å søke, har vi satt ',
            {
              tag: 'span',
              styles: ['bold'],
              children: ['søknadsfristen til 1. juli'],
            },
            '.',
          ],
        },
      ],
      details: [],
    },
    '/jobb/senior-designer': {
      header: 'Bli senior designer i Blank',
      pageImage: {
        path: '/public/val/images/ab527c566aff209cfbc97083b28392c042fc5ae4-1440x811.webp',
        width: 1440,
        height: 811,
        mimeType: 'image/webp',
      },
      contact: {
        person: 'bernholdt',
        title: 'Har du spørsmål om stillingen? ',
        link: {
          text: 'Ta kontakt for en hyggelig prat',
          href: '/kaffeprat',
        },
      },
      applyUrl: {
        text: 'Søk nå!',
        href: 'https://blank.teamtailor.com/jobs/6297263-bli-designer-i-blank',
      },
      pageElements: [
        {
          text: [
            {
              tag: 'p',
              children: [
                'Du vil jobbe med design av digitale produkter og tjenester, og du vil bruke mye tid på lære deg nye ting eller bli enda bedre på ting du allerede kan. Vi i Blank jobber med kjente merkevarer som Amoi, Aidn, ice, reMarkable, NRK, Finn.no, Inspera, og vi jobber med startups som kanskje vil bli like kjente om en stund. I tillegg lager vi egne digitale tjenester – mest fordi vi har lyst.',
              ],
            },
          ],
          title: `Dette vil du jobbe med`,
          type: 'textElement',
        },
        {
          text: [
            {
              tag: 'p',
              children: [
                'Vi tror på å gi folk god lønn og rettferdige betingelser.',
              ],
            },
          ],
          title: `Lønn og betingelser`,
          type: 'textElement',
        },
        {
          text: [
            {
              tag: 'p',
              children: [
                'Du bør være trygg på de prosessene og metodene du trenger til å løse kundenes utfordringer, og du bør ha en trang til å utforske, undersøke og drive fagfeltet vårt fremover. Vi tror på styrken i dybde og mangfold og er ikke interessert i å ansette mer av oss selv. Vi ønsker å bli utfordret, og at du trekker oss i nye retninger.',
              ],
            },
          ],
          title: `Dette ønsker vi fra deg`,
          type: 'textElement',
        },
        {
          text: [
            {
              tag: 'ul',
              children: [
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'p',
                      children: [
                        'Du er nysgjerrig, motivert og selvdreven, fordi vi stoler på deg og gir deg tillit til å kjøre på!',
                      ],
                    },
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'p',
                      children: [
                        'Du må være dyktig til å kommunisere både visuelt og muntlig, fordi det er viktig å kunne overbevise folk om at det du har laget er en sjukt god idé.',
                      ],
                    },
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'p',
                      children: [
                        'Du må ha grundig forståelse for designprosess og metodikk, fordi… les punktet over.',
                      ],
                    },
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'p',
                      children: [
                        'Du må trives med å lede designprosesser fra start til slutt, fordi samskaping er en viktig del av å være designer.',
                      ],
                    },
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'p',
                      children: [
                        'Du må ønske å ta faglig ansvar, både internt og på prosjekt, fordi vi selger kunnskap og da må vi lære noe nytt hele tiden.',
                      ],
                    },
                  ],
                },
                {
                  tag: 'li',
                  children: [
                    {
                      tag: 'p',
                      children: [
                        'Hvis du ikke kan krysse av på alle punktene, stryker du dem og fyller på med noen egne!',
                      ],
                    },
                  ],
                },
              ],
            },
          ],
          title: `Og ellers...`,
          type: 'textElement',
        },
      ],
      pageMetadata: {
        title: 'Bli senior designer i Blank',
        description:
          'Bli med i et prisvinnende fagmiljø av digitale designere som setter agendaen innen digital tjenesteutvikling.',
        image: null,
      },
      benefit: 'ansatt',
      ingress: [
        {
          tag: 'p',
          children: [
            'Bli med i et prisvinnende fagmiljø av digitale designere som setter agendaen innen digital tjenesteutvikling.',
          ],
        },
      ],
      details: [],
    },
    '/jobb/nyutdannet-designer': {
      header: 'Nyutdannet designer for oppstart i 2026',
      benefit: 'ansatt',
      contact: {
        link: { href: '/kaffeprat', text: 'Ta kontakt for en hyggelig prat' },
        title: 'Har du spørsmål om stillingen?',
        person: 'Lena Andersen',
      },
      details: [
        { text: '21 september', title: 'Søknadsfrist' },
        { text: 'fast', title: 'Ansettelsesform' },
      ],
      ingress: [
        {
          tag: 'p',
          children: [
            'Vi tar i mot søknader for nyutdannede interaksjonsdesignere med oppstart i august 2026. ',
          ],
        },
        {
          tag: 'p',
          children: [
            {
              tag: 'span',
              styles: ['bold'],
              children: ['Søknadsfrist 21. september 2025!'],
            },
          ],
        },
      ],
      applyUrl: {
        href: 'https://blank.teamtailor.com/jobs/6297850-blank-soker-nyutdannede-designere-for-oppstart-i-2026',
        text: 'Søk nå',
      },
      pageImage: {
        path: '/public/val/skjermbilde2025-08-21kl.14.43.20_89166.png',
        width: 1458,
        height: 966,
        mimeType: 'image/png',
      },
      pageElements: [
        {
          text: [
            {
              tag: 'p',
              children: [
                'Blank ble startet for å utfordre og vise hvordan et konsulentselskap kan fungere. Her former vi karrierene våre sammen, du får være med å velge prosjekter du faktisk vil jobbe med med et team som hjelper deg i gang. Alle ansatte i Blank får eierskap i selskapet, og har en reell stemme i hvordan vi utvikler oss videre. I Blank vil du jobbe i team sammen med teknologer og designere som bryr seg om faget og om hverandre. Vi deler, diskuterer, og utfordrer. Vi holder et høyt faglig nivå, men lav terskel for å spørre eller si ifra om noe. Vi tror at det beste arbeidsmiljøet er et der folk føler seg trygge, involverte og sett.',
              ],
            },
          ],
          type: 'textElement',
          title: 'Hvorfor Blank?',
        },
        {
          text: [
            {
              tag: 'p',
              children: [
                'Vi tror på iterative utviklingsprosesser, tverrfaglige team, designdrevet produktutvikling og brukersentrerte metoder. Og på hverandre. I Blank jobber vi sammen: ikke bare ved siden av hverandre. Du kommer, sammen med andre dyktige mennesker, til å jobbe med alt fra startups til store selskaper og offentlig sektor. Noen av de vi jobber med er Aneo, Aidn, Finn, Handelshøyskolen BI, NRK, og WeWillWrite.I tillegg utvikler vi egne produkter og tjenester når vi kommer på noe bra – mest fordi vi synes det er gøy. En av disse tjenestene er nå et eget firma med 14 ansatte!',
                { tag: 'br' },
                { tag: 'br' },
                'Du kan se eksempler på våre arbeider ',
                {
                  tag: 'a',
                  href: '/arbeider',
                  children: ['her'],
                },
                '. Tre år på rad er også tjenester Blank har utviklet blitt nominert til Gulltaggen.',
              ],
            },
          ],
          type: 'textElement',
          title: 'Dette vil du jobbe med i Blank',
        },
        {
          text: [
            {
              tag: 'p',
              children: [
                'Som et konsulentselskap, bør det ikke være noen overraskelse at vi er opptatt av faglig utvikling. Mer konkret, har vi blant annet satt av seks dager i året til innedag, en hel dag der du kan lære deg nye ting eller bli enda bedre på ting du allerede kan. I tillegg til dette kan du selvfølgelig dra på konferanser og kurs når ',
                { tag: 'span', styles: ['italic'], children: ['du'] },
                ' mener det er en god ide. Vi har også to konsulentsamlinger i året, design og tech middager, samt hangouts en gang i måneden hvor vi utforsker og lager kule ting sammen.',
              ],
            },
          ],
          type: 'textElement',
          title: 'Fagutvikling i Blank ',
        },
        {
          text: [
            {
              tag: 'p',
              children: [
                'I Blank verdsetter vi søkere med ulik bakgrunn og erfaring. Du trenger ikke ha alle svarene – det viktigste er at du er nysgjerrig og har lyst til å lære sammen med oss.',
                { tag: 'br' },
                { tag: 'br' },
                'Les mer om oss på ',
                { tag: 'a', href: '/', children: ['blank.no'] },
                ', sjekk ut ',
                {
                  tag: 'a',
                  href: 'https://blogg.blank.no/',
                  children: ['bloggen vår'],
                },
                ' eller ta kontakt med ',
                { tag: 'span', styles: ['bold'], children: ['Lena Andersen'] },
                ' på ',
                {
                  tag: 'a',
                  href: 'mailto:lena.andersen@blank.no',
                  children: ['lena.andersen@blank.no'],
                },
                ' om du vil vite mer.',
              ],
            },
          ],
          type: 'textElement',
          title: 'Søk jobb som designer i Blank',
        },
      ],
      pageMetadata: { image: null, title: '', description: '' },
    },
    '/jobb/nyutdannet-teknolog': {
      header: 'Blank søker nyutdannede teknologer for oppstart i 2026',
      benefit: 'ansatt',
      contact: {
        link: { href: '/kaffeprat', text: 'Ta kontakt for en hyggelig prat' },
        title: 'Har du spørsmål om stillingen?',
        person: 'Lena Andersen',
      },
      details: [
        { text: '05. oktober', title: 'Søknadsfrist' },
        { text: 'fast', title: 'Ansettelsesform' },
      ],
      ingress: [
        {
          tag: 'p',
          children: [
            'Vi har startet å lete etter nyutdannede teknologer for oppstart i 2026. ',
          ],
        },
        {
          tag: 'p',
          children: [
            {
              tag: 'span',
              styles: ['bold'],
              children: ['Søknadsfrist 05. oktober 2025!'],
            },
          ],
        },
      ],
      applyUrl: {
        href: 'https://blank.teamtailor.com/jobs/6297606-blank-soker-nyutdannede-teknologer-for-oppstart-i-2026',
        text: 'Søk nå',
      },
      pageImage: {
        path: '/public/val/2021-12innedagpanyloftet-45_3bd73.jpg',
        width: 2048,
        height: 1366,
        mimeType: 'image/jpeg',
      },
      pageElements: [
        {
          text: [
            {
              tag: 'p',
              children: [
                'Blank ble startet for å utfordre og vise hvordan et konsulentselskap kan fungere. Her former vi karrierene våre sammen, du får være med å velge prosjekter du faktisk vil jobbe med med et team som hjelper deg i gang. Alle ansatte i Blank får eierskap i selskapet, og har en reell stemme i hvordan vi utvikler oss videre. I Blank vil du jobbe i team sammen med teknologer og designere som bryr seg om faget og om hverandre. Vi deler, diskuterer, og utfordrer. Vi holder et høyt faglig nivå, men lav terskel for å spørre eller si ifra om noe. Vi tror at det beste arbeidsmiljøet er et der folk føler seg trygge, involverte og sett.',
              ],
            },
          ],
          type: 'textElement',
          title: 'Hvorfor Blank?',
        },
        {
          text: [
            {
              tag: 'p',
              children: [
                'Vi tror på iterative utviklingsprosesser, tverrfaglige team, designdrevet produktutvikling og brukersentrerte metoder. Og på hverandre. I Blank jobber vi sammen: ikke bare ved siden av hverandre. Du kommer, sammen med andre dyktige mennesker, til å jobbe med alt fra startups til store selskaper og offentlig sektor. Noen av de vi jobber med er Aneo, Aidn, Finn, Handelshøyskolen BI, NRK, og WeWillWrite.I tillegg utvikler vi egne produkter og tjenester når vi kommer på noe bra – mest fordi vi synes det er gøy. En av disse tjenestene er nå et eget firma med 14 ansatte!',
                { tag: 'br' },
                { tag: 'br' },
                'Du kan se eksempler på våre arbeider ',
                { tag: 'a', href: '/arbeider', children: ['her'] },
                '. Tre år på rad er også tjenester Blank har utviklet blitt nominert til Gulltaggen.',
              ],
            },
          ],
          type: 'textElement',
          title: 'Dette vil du jobbe med i Blank',
        },
        {
          text: [
            {
              tag: 'p',
              children: [
                'Som et konsulentselskap, bør det ikke være noen overraskelse at vi er opptatt av faglig utvikling. Mer konkret, har vi blant annet satt av seks dager i året til innedag, en hel dag der du kan lære deg nye ting eller bli enda bedre på ting du allerede kan. I tillegg til dette kan du selvfølgelig dra på konferanser og kurs når ',
                { tag: 'span', styles: ['italic'], children: ['du'] },
                ' mener det er en god ide. Vi har også to konsulentsamlinger i året, design og tech middager, samt hangouts en gang i måneden hvor vi utforsker og lager kule ting sammen.',
              ],
            },
          ],
          type: 'textElement',
          title: 'Fagutvikling i Blank',
        },
        {
          text: [
            {
              tag: 'p',
              children: [
                'I Blank verdsetter vi søkere med ulik bakgrunn og erfaring. Du trenger ikke ha alle svarene – det viktigste er at du er nysgjerrig og har lyst til å lære sammen med oss.',
                { tag: 'br' },
                { tag: 'br' },
                'Les mer om oss på ',
                { tag: 'a', href: '/', children: ['blank.no'] },
                ', sjekk ut ',
                {
                  tag: 'a',
                  href: 'https://blogg.blank.no/',
                  children: ['bloggen vår'],
                },
                ' eller ta kontakt med Lena Andersen på ',
                {
                  tag: 'a',
                  href: 'mailto:lena.andersen@blank.no',
                  children: ['lena.andersen@blank.no'],
                },
                ' om du vil vite mer.',
              ],
            },
          ],
          type: 'textElement',
          title: 'Søk jobb som teknolog i Blank!',
        },
      ],
      pageMetadata: { image: null, title: '', description: '' },
    },
  }
);
